/**
 * InfinitSales - useInboxPrefetch (frente C3, flag `inbox_prefetch`)
 *
 * Depois do primeiro paint do inbox atual, em tempo ocioso do navegador
 * (requestIdleCallback, fallback setTimeout de 1500 ms), consulta /status dos
 * OUTROS canais e, nos conectados, busca a primeira pagina (/chats?limit=30),
 * guardando no inboxStore. A troca de inbox passa a abrir com a lista ja na
 * tela.
 *
 * Uma vez por SESSAO (nao por troca de rota): o modulo lembra que ja rodou.
 * Respeita o TTL do store: canal com fetch recente nao e buscado de novo.
 *
 * O canal ATUAL nunca e consultado aqui: o shell dele acabou de pedir o
 * proprio /status e /chats, e repetir a chamada era a duplicata vista na
 * carga fria de 22/09 (por isso nao usa o useActiveConnections, que sempre
 * confere QR e Business). Os status de Instagram e Messenger passam pelo
 * requestCache do service, compartilhado com a NavSidebar.
 *
 * Montado no InboxLayout, que e o pai das rotas de inbox. Depende do
 * `inbox_store` para ter efeito visivel (e la que a lista e lida); sem o
 * store ligado o prefetch so aquece o cache HTTP.
 */
import { useEffect } from 'react';
import { getFeatureFlag } from '../../hooks/useFeatureFlags';
import whatsappService from '../wa-personal/whatsappService';
import waBusinessService from '../wa-business/waBusinessService';
import instagramService from '../instagram/instagramService';
import messengerService from '../messenger/messengerService';
import widgetService from '../widget/widgetService';
import { INBOX_CHANNELS, isInboxStale, getInboxSlice, setInboxSlice } from './inboxStore';

const PREFETCH_LIMIT = 30;
let _ranThisSession = false;

/** Para testes/logout: permite rodar de novo na proxima montagem. */
export function resetInboxPrefetch() {
  _ranThisSession = false;
}

function scheduleIdle(fn) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout: 3000 });
    return () => window.cancelIdleCallback(id);
  }
  const t = setTimeout(fn, 1500);
  return () => clearTimeout(t);
}

// Primeira pagina de cada canal, normalizada como o shell do canal faz ao
// carregar (o Business ordena por last_message_at desc).
const PAGE_FETCHERS = {
  wa_personal: async () => {
    const res = await whatsappService.getChats(PREFETCH_LIMIT, 0, null, null);
    return { chats: res?.data || [], hasMore: Boolean(res?.has_more) };
  },
  wa_business: async () => {
    const res = await waBusinessService.getChats(PREFETCH_LIMIT, 0, null, null);
    const sorted = [...(res?.data || [])].sort((a, b) => {
      const da = new Date(a.last_message_at || a.updated_at || 0);
      const db = new Date(b.last_message_at || b.updated_at || 0);
      return db - da;
    });
    return { chats: sorted, hasMore: Boolean(res?.has_more) };
  },
  instagram: async () => {
    const res = await instagramService.getChats(PREFETCH_LIMIT, 0, {});
    return { chats: res?.data || [], hasMore: Boolean(res?.has_more) };
  },
  messenger: async () => {
    const res = await messengerService.getChats(PREFETCH_LIMIT, 0, {});
    return { chats: res?.data || [], hasMore: Boolean(res?.has_more) };
  },
  widget: async () => {
    const res = await widgetService.getChats(PREFETCH_LIMIT, 0, {});
    return { chats: res?.data || [], hasMore: Boolean(res?.has_more) };
  },
};

// Mesmos endpoints que o useActiveConnections e os shells usam; 404 (canal
// desligado por flag) e erro de rede viram "nao conectado".
const STATUS_FETCHERS = {
  wa_personal: () => whatsappService.getConnectionStatus().then((r) => r?.data || r || null),
  wa_business: () => waBusinessService.getStatus().then((r) => r || null),
  instagram: () => instagramService.getStatus().then((r) => r?.data || null),
  messenger: () => messengerService.getStatus().then((r) => r?.data || null),
  widget: () => widgetService.getStatus().then((r) => r?.data || null),
};

async function prefetchChannel(channel) {
  if (!isInboxStale(channel)) return;
  const antes = getInboxSlice(channel).fetchedAt;
  try {
    const status = await STATUS_FETCHERS[channel]().catch(() => null);
    // So canal conectado (ou inbox virtual do QR) tem lista para aquecer.
    if (!status || !(status.connected || status.has_virtual_inbox)) return;
    const page = await PAGE_FETCHERS[channel]();
    // Se o shell do canal montou e carregou enquanto isto voava, nao pisa.
    if (getInboxSlice(channel).fetchedAt !== antes) return;
    setInboxSlice(channel, {
      chats: page.chats,
      hasMore: page.hasMore,
      page: 1,
      filters: null,
      status,
      fetchedAt: Date.now(),
    });
  } catch {
    // best-effort: o shell do canal carrega normalmente ao ser aberto
  }
}

/**
 * @param {string|null} currentChannel canal do inbox aberto agora (excluido)
 */
export default function useInboxPrefetch(currentChannel) {
  const enabled = getFeatureFlag('inbox_prefetch');

  useEffect(() => {
    if (!enabled || _ranThisSession || !currentChannel) return undefined;
    return scheduleIdle(() => {
      if (_ranThisSession) return;
      _ranThisSession = true;
      // Uma vez por sessao: o canal atual da PRIMEIRA rodada e o excluido.
      INBOX_CHANNELS
        .filter((c) => c !== currentChannel)
        .forEach((c) => { prefetchChannel(c); });
    });
  }, [enabled, currentChannel]);
}
